import { useMemo } from 'react';

import { IServerSideDatasource } from 'ag-grid-community';
import { PencilIcon } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';

import { AccountsCombobox } from '@/components/common/accounts-combobox';
import { SitesCombobox } from '@/components/common/sites-combobox';
import { Button } from '@/components/ui/button';
import { ColDef } from '@/grid/types';

import { useEmbryoAppContext } from '../../context/EmbryoAppContext';
import { EmbryoGrid } from '../EmbryoGrid';
import { GlobalSearch } from '../GlobalSearch';
import { ShippingIntegrationTab } from '../ShippingIntegrationTab';

interface TabContentProps {
  columnDefs: ColDef<any>[];
  storageKey: string;
  gridClassName: string;
  dataSource: IServerSideDatasource;
  onGridReady?: (api: { refresh: () => void; deselectAll: () => void }) => void;
  onEditSelected?: () => void;
  onDeactivateSelected?: () => void;
}

export const TabContent = ({
  columnDefs,
  storageKey,
  gridClassName,
  dataSource,
  onGridReady,
  onEditSelected,
  onDeactivateSelected,
}: TabContentProps) => {
  const { filters, updateFilter, selectedEmbryos } = useEmbryoAppContext();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedSites = useMemo(
    () => searchParams.getAll('siteId'),
    [searchParams],
  );

  if (location.pathname.includes('shipping-integration')) {
    return <ShippingIntegrationTab />;
  }

  return (
    <div className="embryo-tab-content flex flex-col gap-4">
      <div className="mx-3 flex items-center gap-2">
        {!location.pathname.includes('outside-embryos') && (
          <>
            <SitesCombobox
              multiple
              value={selectedSites}
              onChange={(siteIds) => {
                setSearchParams(
                  (prev) => {
                    prev.delete('siteId');

                    for (const siteId of siteIds) {
                      prev.append('siteId', siteId);
                    }

                    return prev;
                  },
                  { replace: true },
                );
              }}
            />
            <AccountsCombobox
              value={filters.ownerId}
              onChange={(accountId) => {
                updateFilter('ownerId', accountId);
                setSearchParams(
                  (prev) => {
                    if (accountId) {
                      prev.set('ownerId', accountId);
                    } else {
                      prev.delete('ownerId');
                    }
                    return prev;
                  },
                  { replace: true },
                );
              }}
            />
          </>
        )}

        <GlobalSearch />
        <Button
          variant="outline"
          size="sm"
          disabled={selectedEmbryos.length === 0}
          onClick={onEditSelected}
          className="relative ml-auto"
        >
          <PencilIcon data-icon="inline-start" />
          Edit Selected
          {selectedEmbryos.length > 0 && (
            <span className="absolute -top-2 -right-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-600 px-1 text-xs font-semibold text-white">
              {selectedEmbryos.length}
            </span>
          )}
        </Button>
      </div>
      <EmbryoGrid
        key={storageKey}
        columnDefs={columnDefs}
        storageKey={storageKey}
        gridClassName={gridClassName}
        dataSource={dataSource}
        filters={filters}
        onGridReady={onGridReady}
        onDeactivateSelected={onDeactivateSelected}
      />
    </div>
  );
};
